import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiErrorMessage } from "../../api/client";
import { chatApi } from "../api";
import { UI, useLang, type Bilingual } from "../lang";

/** The thread line's `meta.spec_card` (agent-library Task 7). */
export interface SpecCardMeta {
  agent_id: string;
  name: Bilingual;
  in_words: Bilingual;
  /** The five access lines code wrote: reads, sensitivity, who sees it, what it may do, how often. */
  access: { en: string[]; ar: string[] };
  status: string;
  /** The agent's version the card was written from; a chip tapped on an older card is refused. */
  version?: number;
  /** "Change something" chips, written by code from the registry (agent-library Task 9). */
  edits?: { key: string; label: Bilingual }[];
  /** "Share with…" (agent-library Task 13): on an approved agent, the roles it isn't shared with yet. */
  share?: { roles: { key: string; label: Bilingual }[] };
  /**
   * "Publish to the library" (agent-library Task 13b): offered on an Active
   * agent; `entry` is its library entry, when it has one.
   */
  library?: {
    may_publish: boolean;
    roles: { key: string; label: Bilingual }[];
    entry: { id: string; status: "pending" | "published" | "unpublished"; version: number; roles: string[] } | null;
  };
}

/** Where the agent is now, in words (the thread re-reads its status from the row). */
const STATUS: Record<string, Bilingual> = {
  awaiting_access: { en: "Waiting on your Supervisor.", ar: "بانتظار المشرف." },
  trial: { en: "Running as a Trial: ask for it any time.", ar: "يعمل كتجربة: اطلبه متى شئت." },
  active: { en: "Active: ask for it any time.", ar: "نشط: اطلبه متى شئت." },
  held: { en: "On hold. Ask for access again, or change something.", ar: "موقوف مؤقتاً. اطلب الوصول مجدداً أو غيّر شيئاً." },
  retired: { en: "Retired.", ar: "متوقف نهائياً." },
};
/** Access is still to be asked for only while it is a draft or on hold. */
const MAY_ASK = new Set(["draft", "held"]);
const ASKED_AGAIN: Bilingual = {
  en: "This changes what it can access, so your Supervisor will be asked again.",
  ar: "هذا يغيّر ما يمكنه الوصول إليه، لذا سيُطلب من المشرف الموافقة مجدداً.",
};

const SHARE = {
  open: { en: "Share with…", ar: "شارك مع…" },
  roles: { en: "Roles", ar: "الأدوار" },
  send: { en: "Ask your Supervisor", ar: "اسأل المشرف" },
  sent: { en: "Sent to your Supervisor. Nobody new sees it until they approve.", ar: "أُرسل إلى المشرف. لن يراه أحد جديد قبل موافقته." },
} satisfies Record<string, Bilingual>;

const PUBLISH = {
  open: { en: "Publish to the library", ar: "انشر في المكتبة" },
  roles: { en: "Who may reuse it", ar: "من يمكنه إعادة استخدامه" },
  send: { en: "Publish", ar: "انشر" },
  inLibrary: { en: "In the library.", ar: "في المكتبة." },
  pending: { en: "Waiting on your Supervisor to publish it.", ar: "بانتظار موافقة المشرف على نشره." },
  takeOut: { en: "Take it out of the library", ar: "أزله من المكتبة" },
} satisfies Record<string, Bilingual>;

/** The server's refusal, in the reader's language when it sent both. */
export function refusal(e: unknown, lang: string): string {
  const ar = e instanceof ApiError ? (e.body as { detail_ar?: unknown } | null)?.detail_ar : undefined;
  return lang === "ar" && typeof ar === "string" ? ar : apiErrorMessage(e);
}

/**
 * A new agent before it runs: what it will do, in words, and what it will
 * access, in the five lines the Supervisor will also see. Both come from
 * code; nothing here is the model's.
 */
export function SpecCard({ workspaceId, card }: { workspaceId: string; card: SpecCardMeta }) {
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const [notNow, setNotNow] = useState(false);
  const [changing, setChanging] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const ask = useMutation({
    mutationFn: () => chatApi.requestAccess(workspaceId, card.agent_id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] }),
  });
  // A chip: code applies it on the server and the thread re-reads the card.
  const edit = useMutation({
    mutationFn: (key: string) =>
      chatApi.editAgent(workspaceId, card.agent_id, { key, ...(card.version !== undefined ? { version: card.version } : {}) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] }),
  });
  // Sharing is an access change (Task 13): the chosen roles go to the
  // Supervisors as a request; nobody new sees it until one approves.
  const share = useMutation({
    mutationFn: () => chatApi.shareAgent(workspaceId, card.agent_id, roles),
    onSuccess: () => {
      setSharing(false);
      setRoles([]);
      void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] });
    },
  });
  // Publishing (Task 13b): who may reuse it, every role to begin with, or the entry's.
  const [publishing, setPublishing] = useState(false);
  const entry = card.library?.entry ?? null;
  const [reuse, setReuse] = useState<string[]>(
    entry?.status === "published" ? entry.roles : (card.library?.roles.map((r) => r.key) ?? []),
  );
  const publish = useMutation({
    mutationFn: () => chatApi.publishAgent(workspaceId, card.agent_id, reuse),
    onSuccess: () => {
      setPublishing(false);
      void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] });
    },
  });
  const takeOut = useMutation({
    mutationFn: () => chatApi.unpublishEntry(workspaceId, entry!.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] }),
  });
  const mayPublish = card.library?.may_publish === true;
  const toggleReuse = (r: string) => setReuse((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  const mayShare = (card.status === "trial" || card.status === "active") && (card.share?.roles.length ?? 0) > 0;
  const toggle = (r: string) => setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  const lines = lang === "ar" && card.access.ar.length > 0 ? card.access.ar : card.access.en;
  const open = MAY_ASK.has(card.status) && !notNow && !ask.isSuccess;
  const mayChange = card.status !== "retired" && (card.edits?.length ?? 0) > 0;
  const status = STATUS[card.status];
  const changeButton = (
    <button type="button" className={`pwa-btn${changing ? " on" : ""}`} aria-expanded={changing} onClick={() => setChanging((c) => !c)}>
      {t({ en: "Change something", ar: "غيّر شيئاً" })}
    </button>
  );

  return (
    <div className="pwa-card pwa-spec" data-testid={`spec-${card.agent_id}`}>
      <div className="ttl">{t(card.name)}</div>
      <div style={{ margin: "4px 0" }}>{t(card.in_words)}</div>
      <div className="pwa-fork-prepared-label">{t({ en: "What it will access", ar: "ما سيصل إليه" })}</div>
      <ul className="pwa-spec-access">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      {status ? (
        <div className="sub" data-testid={`spec-status-${card.agent_id}`}>
          {t(status)}
        </div>
      ) : null}
      {open ? (
        <div className="pwa-choices">
          <button type="button" className="pwa-btn primary" disabled={ask.isPending} onClick={() => ask.mutate()}>
            {t({ en: "Ask for access", ar: "اطلب الوصول" })}
          </button>
          {mayChange ? changeButton : null}
          <button type="button" className="pwa-btn" onClick={() => setNotNow(true)}>
            {t(UI.notNow)}
          </button>
        </div>
      ) : mayChange || mayShare || mayPublish ? (
        <div className="pwa-choices">
          {mayChange ? changeButton : null}
          {mayShare ? (
            <button type="button" className={`pwa-btn${sharing ? " on" : ""}`} aria-expanded={sharing} onClick={() => setSharing((s) => !s)}>
              {t(SHARE.open)}
            </button>
          ) : null}
          {mayPublish ? (
            <button
              type="button"
              className={`pwa-btn${publishing ? " on" : ""}`}
              aria-expanded={publishing}
              onClick={() => setPublishing((p) => !p)}
            >
              {t(PUBLISH.open)}
            </button>
          ) : null}
        </div>
      ) : null}
      {publishing && mayPublish ? (
        <>
          <div className="pwa-chips" role="group" aria-label={t(PUBLISH.roles)}>
            {card.library!.roles.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`pwa-chip${reuse.includes(r.key) ? " on" : ""}`}
                aria-pressed={reuse.includes(r.key)}
                onClick={() => toggleReuse(r.key)}
              >
                {t(r.label)}
              </button>
            ))}
          </div>
          <div className="pwa-choices">
            <button type="button" className="pwa-btn primary" disabled={reuse.length === 0 || publish.isPending} onClick={() => publish.mutate()}>
              {t(PUBLISH.send)}
            </button>
          </div>
        </>
      ) : null}
      {publish.isSuccess ? (
        <div className="sub" role="status">
          {lang === "ar" ? publish.data.message.body_ar : publish.data.message.body_en}
        </div>
      ) : null}
      {publish.isError ? (
        <div className="pwa-fork-effect" role="status">
          {refusal(publish.error, lang)}
        </div>
      ) : null}
      {entry?.status === "pending" && !publish.isSuccess ? <div className="sub">{t(PUBLISH.pending)}</div> : null}
      {entry?.status === "published" ? (
        <div className="pwa-choices">
          <span className="sub">{t(PUBLISH.inLibrary)}</span>
          <button type="button" className="pwa-btn" disabled={takeOut.isPending || takeOut.isSuccess} onClick={() => takeOut.mutate()}>
            {t(PUBLISH.takeOut)}
          </button>
        </div>
      ) : null}
      {takeOut.isError ? (
        <div className="pwa-fork-effect" role="status">
          {refusal(takeOut.error, lang)}
        </div>
      ) : null}
      {sharing && mayShare ? (
        <>
          <div className="pwa-chips" role="group" aria-label={t(SHARE.roles)}>
            {card.share!.roles.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`pwa-chip${roles.includes(r.key) ? " on" : ""}`}
                aria-pressed={roles.includes(r.key)}
                onClick={() => toggle(r.key)}
              >
                {t(r.label)}
              </button>
            ))}
          </div>
          <div className="pwa-choices">
            <button type="button" className="pwa-btn primary" disabled={roles.length === 0 || share.isPending} onClick={() => share.mutate()}>
              {t(SHARE.send)}
            </button>
          </div>
        </>
      ) : null}
      {share.isSuccess ? (
        <div className="sub" role="status">
          {t(SHARE.sent)}
        </div>
      ) : null}
      {share.isError ? (
        <div className="pwa-fork-effect" role="status">
          {refusal(share.error, lang)}
        </div>
      ) : null}
      {changing && mayChange ? (
        <div className="pwa-chips" role="group" aria-label={t({ en: "Changes", ar: "التغييرات" })}>
          {card.edits!.map((e) => (
            <button key={e.key} type="button" className="pwa-chip" disabled={edit.isPending} onClick={() => edit.mutate(e.key)}>
              {t(e.label)}
            </button>
          ))}
        </div>
      ) : null}
      {edit.isSuccess && edit.data.access_changed && edit.data.request_id ? (
        <div className="sub" role="status">
          {t(ASKED_AGAIN)}
        </div>
      ) : null}
      {edit.isError ? (
        <div className="pwa-fork-effect" role="status">
          {apiErrorMessage(edit.error)}
        </div>
      ) : null}
      {notNow ? <div className="sub">{t({ en: "Kept as a draft.", ar: "محفوظ كمسودة." })}</div> : null}
      {ask.isError ? (
        <div className="pwa-fork-effect" role="status">
          {apiErrorMessage(ask.error)}
        </div>
      ) : null}
    </div>
  );
}
